// src/screens/AllTrainings.tsx
import { useEffect, useState } from "react";
import { LogoLoader } from "../components/LogoLoader";
import { supabase } from "../supabaseClient";
import { Link, useNavigate } from "react-router-dom";
import {
  PlusCircle, CalendarDays, ChevronRight, Lock, ListFilter, PieChart as PieIcon,
  Clock, NotebookPen, X, ChevronLeft, ChevronsLeft, ChevronsRight, Target
} from "lucide-react";
import i18n from "../i18n";

type Training = {
  id: string;
  date: string;
  notes: string;
  team_id: string;
  user_id?: string | null; // ⬅️ fallback si aucune team visible
};
type Attendance = { training_id: string; player_id: string; was_present: boolean; };
type Team = { id: string; team_name: string };
type Mode = "past" | "upcoming" | "all";

const PAGE_SIZE = 20;

/* ===== i18n mini-hook ===== */
function useI18nSafe() {
  const [lng, setLng] = useState((i18n.resolvedLanguage || i18n.language || "fr").toLowerCase());
  useEffect(() => {
    const onChange = (l: string) => setLng(l.toLowerCase());
    i18n.on("languageChanged", onChange);
    return () => i18n.off("languageChanged", onChange);
  }, []);
  const t = (key: string, options?: Record<string, unknown>): string =>
    options != null ? String((i18n.t as any)(key, options)) : String((i18n.t as any)(key));
  return { t, lng };
}

export const AllTrainings = () => {
  const { t, lng } = useI18nSafe();

  const [trainings, setTrainings] = useState<Training[]>([]);
  const [attendance, setAttendance] = useState<Attendance[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [selectedTeamId, setSelectedTeamId] = useState<string>("all");
  const [loading, setLoading] = useState(true);
  const [isPremium, setIsPremium] = useState(false);

  // Filtres
  const [mode, setMode] = useState<Mode>("past"); // par défaut: passés
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");

  // Pagination
  const [page, setPage] = useState<number>(1); // 1-based
  const [total, setTotal] = useState<number | null>(null);

  // Mobile UI
  const [filtersOpen, setFiltersOpen] = useState(false);

  const navigate = useNavigate();

  // utils
  const startOfDayIso = (d: Date) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x.toISOString(); };
  const endOfDayIso = (d: Date) => { const x = new Date(d); x.setHours(23, 59, 59, 999); return x.toISOString(); };
  const nowIso = () => new Date().toISOString();
  const hasTime = (iso: string) => /T\d{2}:\d{2}/.test(iso);

  // Charge équipes (visibles via RLS) + attendance une fois
  useEffect(() => {
    const init = async () => {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { setLoading(false); return; }

      const { data: profile } = await supabase
        .from("user_profiles")
        .select("is_premium")
        .eq("id", user.id)
        .single();
      setIsPremium(!!profile?.is_premium);

      // ⬇️ toutes les équipes visibles (owner + membre via RLS)
      const { data: teamsData, error: teamErr } = await supabase
        .from("teams")
        .select("id, team_name")
        .or("archived.is.null,archived.eq.false")
        .order("team_name", { ascending: true });
      if (teamErr) {
        console.warn("[AllTrainings] teams fetch error:", teamErr);
        setTeams([]);
      } else {
        setTeams(teamsData || []);
      }

      const { data: attendanceData } = await supabase
        .from("training_attendance")
        .select("training_id, player_id, was_present");
      if (attendanceData) setAttendance(attendanceData);

      setLoading(false);
    };
    init();
  }, []);

  // Reset page à 1 quand un filtre change
  useEffect(() => {
    setPage(1);
  }, [selectedTeamId, mode, startDate, endDate]);

  // Charge trainings selon filtres + pagination
  useEffect(() => {
    const fetchTrainings = async () => {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { setLoading(false); return; }

      let query = supabase
        .from("trainings")
        .select("*", { count: "exact" }); // total

      // ⬇️ Filtre d'équipe : all => toutes les équipes visibles ; sinon eq
      const teamIds = teams.map(t => t.id);
      if (selectedTeamId !== "all") {
        query = query.eq("team_id", selectedTeamId);
      } else if (teamIds.length > 0) {
        query = query.in("team_id", teamIds);
      } else {
        // Fallback si aucune équipe visible (rare) : tes trainings “perso”
        query = query.eq("user_id", user.id);
      }

      // ⬇️ Filtre période
      const hasRange = !!startDate && !!endDate;
      if (hasRange) {
        const gteIso = startOfDayIso(new Date(startDate));
        const lteIso = endOfDayIso(new Date(endDate));
        query = query.gte("date", gteIso).lte("date", lteIso).order("date", { ascending: false });
      } else {
        if (mode === "past") {
          query = query.lte("date", nowIso()).order("date", { ascending: false });
        } else if (mode === "upcoming") {
          query = query.gte("date", nowIso()).order("date", { ascending: true });
        } else {
          query = query.order("date", { ascending: false });
        }
      }

      // ⬇️ Pagination
      const from = (page - 1) * PAGE_SIZE;
      const to = from + PAGE_SIZE - 1;
      const { data, count, error } = await query.range(from, to);

      if (!error) {
        setTrainings((data ?? []) as Training[]);
        setTotal(typeof count === "number" ? count : null);
      } else {
        console.error("[AllTrainings] trainings fetch error:", error);
        setTrainings([]);
        setTotal(null);
      }
      setLoading(false);
    };

    fetchTrainings();
  }, [teams, selectedTeamId, mode, startDate, endDate, page]);

  const getPresentCount = (trainingId: string) =>
    attendance.filter((a) => a.training_id === trainingId && a.was_present).length;

  const getTeamName = (teamId: string) =>
    teams.find((t) => t.id === teamId)?.team_name ?? t("alltrainings:unknown_team");

  const clearRange = () => { setStartDate(""); setEndDate(""); };

  const totalPages = total ? Math.max(1, Math.ceil(total / PAGE_SIZE)) : 1;
  const fromIndex = (page - 1) * PAGE_SIZE + 1;
  const toIndex = total ? Math.min(page * PAGE_SIZE, total) : (fromIndex - 1 + trainings.length);

  // ---- Helpers de date localisés ----
  const locale = lng === "fr" ? "fr-FR" : "en-US";
  const formatDateLong = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleDateString(locale, { weekday: "long", year: "numeric", month: "long", day: "numeric" });
  };
  const formatTimeHM = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" });
  };

  if (loading) {
    return <LogoLoader />;
  }
  return (
    <div className="min-h-screen w-full p-4 bg-gray-50 text-gray-900 dark:bg-gray-950 dark:text-gray-100">
      {/* Barre haute */}
      <div
        className="sticky z-10 mb-4"
        style={{ top: "calc(env(safe-area-inset-top) + 12px)" }}
      >
        <div className="max-w-4xl mx-auto flex flex-col gap-2">
          {/* Ligne: bouton créer + toggle filtres mobile */}
          <div className="flex items-stretch gap-2">
            <div className="flex-1 rounded-2xl border px-3 py-2 bg-white/80 border-gray-200 backdrop-blur dark:bg-gray-900/80 dark:border-gray-800">
              <button
                onClick={() => navigate("/createtraining")}
                className="w-full sm:w-auto bg-orange-500 hover:bg-orange-600 text-white font-semibold px-4 py-2 rounded flex items-center justify-center gap-2 focus:outline-none focus:ring-2 focus:ring-orange-400/60"
              >
                <PlusCircle className="w-5 h-5" />
                <span className="hidden xs:inline">{t("alltrainings:actions.add")}</span>
              </button>
            </div>

            {/* Toggle filtres (mobile) */}
            <button
              type="button"
              onClick={() => setFiltersOpen((v) => !v)}
              className="sm:hidden shrink-0 rounded-2xl border px-3 py-2 bg-white/80 border-gray-200 backdrop-blur dark:bg-gray-900/80 dark:border-gray-800 inline-flex items-center gap-2"
              aria-expanded={filtersOpen}
              aria-controls="filters-panel"
              title={t("alltrainings:filters.toggle_title")}
            >
              <ListFilter className="w-5 h-5" />
              <span>{t("alltrainings:filters.title")}</span>
            </button>
          </div>

          {/* Filtres MOBILE */}
          <div
            id="filters-panel"
            className={`sm:hidden ${filtersOpen ? "block" : "hidden"} rounded-2xl border bg-white/80 border-gray-200 backdrop-blur dark:bg-gray-900/80 dark:border-gray-800 p-3`}
          >
            <div className="flex flex-col gap-3">
              {/* Équipe */}
              <label className="text-sm">{t("alltrainings:filters.team_label")}</label>
              <select
                value={selectedTeamId}
                onChange={(e) => setSelectedTeamId(e.target.value)}
                className="w-full border rounded px-3 py-2 text-sm bg-white border-gray-300 text-gray-900 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100"
              >
                <option value="all">{t("alltrainings:filters.teams.all")}</option>
                {teams.map((t) => (
                  <option key={t.id} value={t.id}>{t.team_name}</option>
                ))}
              </select>

              {/* Mode */}
              <label className="text-sm">{t("alltrainings:filters.period_label")}</label>
              <select
                value={mode}
                onChange={(e) => setMode(e.target.value as Mode)}
                className="w-full border rounded px-3 py-2 text-sm bg-white border-gray-300 text-gray-900 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100"
              >
                <option value="past">{t("alltrainings:filters.period.past")}</option>
                <option value="upcoming">{t("alltrainings:filters.period.upcoming")}</option>
                <option value="all">{t("alltrainings:filters.period.all")}</option>
              </select>

              {/* Tranche de dates */}
              <div className="flex flex-col gap-2">
                <label className="text-sm">{t("alltrainings:filters.range_label")}</label>
                <div className="flex flex-col xs:flex-row gap-2">
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="flex-1 border rounded px-3 py-2 text-sm bg-white border-gray-300 text-gray-900 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100"
                  />
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="flex-1 border rounded px-3 py-2 text-sm bg-white border-gray-300 text-gray-900 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100"
                  />
                  <button
                    type="button"
                    onClick={() => { setStartDate(""); setEndDate(""); }}
                    className="inline-flex items-center justify-center gap-1 px-3 py-2 rounded border text-sm border-gray-200 text-gray-700 hover:bg-gray-50 dark:border-gray-800 dark:text-gray-300 dark:hover:bg-gray-800/60"
                    title={t("alltrainings:filters.range_reset_title")}
                  >
                    <X className="w-4 h-4" />
                    <span className="hidden xs:inline">{t("common:reset")}</span>
                  </button>
                </div>
              </div>

              {/* Liens actions */}
              <div className="flex flex-wrap items-center gap-2">
                {isPremium ? (
                  <Link
                    to={`/attendance${selectedTeamId !== "all" ? `?team_id=${selectedTeamId}` : ""}`}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded border text-sm border-purple-200 text-purple-700 hover:bg-purple-50 dark:border-purple-900/40 dark:text-purple-300 dark:hover:bg-purple-900/20"
                  >
                    <PieIcon className="w-4 h-4" />
                    <span className="hidden xs:inline">{t("alltrainings:actions.attendance_stats")}</span>
                  </Link>
                ) : (
                  <button
                    type="button"
                    className="inline-flex items-center gap-2 px-3 py-2 rounded border text-sm cursor-not-allowed opacity-70 border-gray-300 text-gray-500 bg-gray-50 dark:border-gray-700 dark:text-gray-400 dark:bg-gray-800"
                    onClick={() => alert(t("alltrainings:premium_only_alert"))}
                  >
                    <Lock className="w-4 h-4" />
                    <span className="hidden xs:inline">{t("alltrainings:actions.attendance_stats")}</span>
                  </button>
                )}

                {/* Shooting totals (mobile) */}
                <Link
                  to={`/shooting-totals${selectedTeamId !== "all" ? `?team_id=${selectedTeamId}` : ""}`}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded border text-sm border-emerald-200 text-emerald-700 hover:bg-emerald-50 dark:border-emerald-900/40 dark:text-emerald-300 dark:hover:bg-emerald-900/20"
                  title={t("alltrainings:actions.shooting_totals_title")}
                >
                  <Target className="w-4 h-4" />
                  <span className="hidden xs:inline">{t("alltrainings:actions.shooting_totals")}</span>
                </Link>

                <Link
                  to="/block-templates"
                  className="inline-flex items-center gap-2 px-3 py-2 rounded border text-sm border-amber-200 text-amber-700 hover:bg-amber-50 dark:border-amber-900/40 dark:text-amber-300 dark:hover:bg-amber-900/20"
                >
                  <NotebookPen className="w-4 h-4" />
                  <span className="hidden xs:inline">{t("alltrainings:actions.block_templates")}</span>
                </Link>
              </div>
            </div>
          </div>

          {/* Filtres DESKTOP */}
          <div className="hidden sm:flex flex-col gap-2">
            {/* Ligne 1 : Équipe + Mode */}
            <div className="inline-flex items-center gap-2 rounded-2xl border px-3 py-2 bg-white/80 border-gray-200 backdrop-blur dark:bg-gray-900/80 dark:border-gray-800 overflow-x-auto">
              <ListFilter className="w-5 h-5 text-gray-600 dark:text-gray-400 shrink-0" />
              <label htmlFor="teamFilter" className="text-sm text-gray-700 dark:text-gray-300 shrink-0">
                {t("alltrainings:filters.team_label")}
              </label>
              <select
                id="teamFilter"
                value={selectedTeamId}
                onChange={(e) => setSelectedTeamId(e.target.value)}
                className="border rounded px-3 py-2 text-sm bg-white border-gray-300 text-gray-900 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100"
              >
                <option value="all">{t("alltrainings:filters.teams.all")}</option>
                {teams.map((t) => (
                  <option key={t.id} value={t.id}>{t.team_name}</option>
                ))}
              </select>

              <div className="ml-2 inline-flex rounded-lg overflow-hidden border border-gray-200 dark:border-gray-800 text-sm shrink-0">
                <button
                  type="button"
                  onClick={() => setMode("past")}
                  className={`px-3 py-2 ${mode === "past" ? "bg-blue-600 text-white" : "bg-transparent text-gray-700 dark:text-gray-300"}`}
                  title={t("alltrainings:filters.period.past_title")}
                >
                  {t("alltrainings:filters.period.past")}
                </button>
                <button
                  type="button"
                  onClick={() => setMode("upcoming")}
                  className={`px-3 py-2 ${mode === "upcoming" ? "bg-blue-600 text-white" : "bg-transparent text-gray-700 dark:text-gray-300"}`}
                  title={t("alltrainings:filters.period.upcoming_title")}
                >
                  {t("alltrainings:filters.period.upcoming")}
                </button>
                <button
                  type="button"
                  onClick={() => setMode("all")}
                  className={`px-3 py-2 ${mode === "all" ? "bg-blue-600 text-white" : "bg-transparent text-gray-700 dark:text-gray-300"}`}
                  title={t("alltrainings:filters.period.all_title")}
                >
                  {t("alltrainings:filters.period.all")}
                </button>
              </div>
            </div>

            {/* Ligne 2 : Tranche de dates + actions */}
            <div className="inline-flex items-center gap-2 rounded-2xl border px-3 py-2 bg-white/80 border-gray-200 backdrop-blur dark:bg-gray-900/80 dark:border-gray-800 flex-wrap">
              <CalendarDays className="w-5 h-5 text-blue-600 dark:text-blue-400 shrink-0" />
              <span className="text-sm">{t("alltrainings:filters.range.from")}</span>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="border rounded px-3 py-2 text-sm bg-white border-gray-300 text-gray-900 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100"
              />
              <span className="text-sm">{t("alltrainings:filters.range.to")}</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="border rounded px-3 py-2 text-sm bg-white border-gray-300 text-gray-900 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-100"
              />
              <button
                type="button"
                onClick={clearRange}
                className="ml-1 inline-flex items-center gap-1 px-2 py-2 rounded border text-sm border-gray-200 text-gray-700 hover:bg-gray-50 dark:border-gray-800 dark:text-gray-300 dark:hover:bg-gray-800/60"
                title={t("alltrainings:filters.range_reset_title")}
              >
                <X className="w-4 h-4" />
                {t("common:reset")}
              </button>

              <div className="flex items-center gap-2 ml-auto flex-wrap">
                {isPremium ? (
                  <Link
                    to={`/attendance${selectedTeamId !== "all" ? `?team_id=${selectedTeamId}` : ""}`}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded border text-sm border-purple-200 text-purple-700 hover:bg-purple-50 dark:border-purple-900/40 dark:text-purple-300 dark:hover:bg-purple-900/20"
                    title={t("alltrainings:actions.attendance_title")}
                  >
                    <PieIcon className="w-4 h-4" />
                    {t("alltrainings:actions.attendance_stats")}
                  </Link>
                ) : (
                  <button
                    type="button"
                    className="inline-flex items-center gap-2 px-3 py-2 rounded border text-sm cursor-not-allowed opacity-70 border-gray-300 text-gray-500 bg-gray-50 dark:border-gray-700 dark:text-gray-400 dark:bg-gray-800"
                    onClick={() => alert(t("alltrainings:premium_only_alert"))}
                    title={t("alltrainings:premium_only_title")}
                  >
                    <Lock className="w-4 h-4" />
                    {t("alltrainings:actions.attendance_stats")}
                  </button>
                )}

                {/* Shooting totals (desktop) */}
                <Link
                  to={`/shooting-totals${selectedTeamId !== "all" ? `?team_id=${selectedTeamId}` : ""}`}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded border text-sm border-emerald-200 text-emerald-700 hover:bg-emerald-50 dark:border-emerald-900/40 dark:text-emerald-300 dark:hover:bg-emerald-900/20"
                  title={t("alltrainings:actions.shooting_totals_title")}
                >
                  <Target className="w-4 h-4" />
                  {t("alltrainings:actions.shooting_totals")}
                </Link>

                <Link
                  to="/block-templates"
                  className="inline-flex items-center gap-2 px-3 py-2 rounded border text-sm border-amber-200 text-amber-700 hover:bg-amber-50 dark:border-amber-900/40 dark:text-amber-300 dark:hover:bg-amber-900/20"
                  title={t("alltrainings:actions.blocks_title")}
                >
                  <NotebookPen className="w-4 h-4" />
                  {t("alltrainings:actions.block_templates")}
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <h1 className="max-w-4xl mx-auto text-2xl font-bold mb-6 flex items-center gap-2">
        <CalendarDays className="w-6 h-6 text-blue-600 dark:text-blue-400" />
        {t("alltrainings:title")}
      </h1>

      {loading ? (
        <p className="text-gray-600 dark:text-gray-300">{t("common:loading")}</p>
      ) : trainings.length === 0 ? (
        <p className="text-gray-600 dark:text-gray-400">{t("alltrainings:empty")}</p>
      ) : (
        <>
          <div className="space-y-4 max-w-4xl mx-auto">
            {trainings.map((training) => {
              const dateLabel = formatDateLong(training.date);
              const timeLabel = hasTime(training.date) ? formatTimeHM(training.date) : null;
              const present = getPresentCount(training.id);
              const notesPreview = training.notes ? training.notes.slice(0, 120) : t("alltrainings:no_notes");
              const teamName = getTeamName(training.team_id);

              return (
                <Link
                  key={training.id}
                  to={`/training/${training.id}`}
                  className="block rounded-2xl border p-4 shadow-sm transition bg-white hover:bg-gray-50 border-gray-200 dark:bg-gray-900 dark:hover:bg-gray-800/60 dark:border-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                >
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 mb-2">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-3">
                      <span className="truncate">{dateLabel}</span>
                      {timeLabel && (
                        <span className="inline-flex items-center gap-1 text-sm text-gray-600 dark:text-gray-400">
                          <Clock className="w-4 h-4" />
                          {timeLabel}
                        </span>
                      )}
                    </h2>
                    <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                      <span className="inline-flex items-center px-2 py-0.5 rounded border bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-800/40">
                        {teamName}
                      </span>
                      <span>
                        {t("alltrainings:presents_count", { count: present })}
                      </span>
                      <ChevronRight className="w-4 h-4" />
                    </div>
                  </div>
                  <p className="text-gray-700 dark:text-gray-300 italic line-clamp-3 sm:line-clamp-none">
                    {notesPreview}
                    {training.notes && training.notes.length > 120 ? "…" : ""}
                  </p>
                </Link>
              );
            })}
          </div>

          {/* Pagination */}
          <div className="max-w-4xl mx-auto mt-6 flex items-center justify-between flex-wrap gap-2">
            <div className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 min-w-0">
              {total !== null ? (
                <>
                  {t("alltrainings:pagination.displaying", {
                    from: fromIndex,
                    to: toIndex,
                    total,
                  })}
                </>
              ) : (
                <>
                  {t("alltrainings:pagination.page", { page })}
                </>
              )}
            </div>
            <div className="flex items-center gap-1 sm:gap-2">
              <button
                onClick={() => setPage(1)}
                disabled={page === 1}
                className="hidden sm:inline-flex p-2 rounded border disabled:opacity-50 bg-white border-gray-200 hover:bg-gray-50 dark:bg-gray-900 dark:border-gray-800 dark:hover:bg-gray-800/60"
                title={t("alltrainings:pagination.first_title")}
              >
                <ChevronsLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="p-2 rounded border disabled:opacity-50 bg-white border-gray-200 hover:bg-gray-50 dark:bg-gray-900 dark:border-gray-800 dark:hover:bg-gray-800/60"
                title={t("alltrainings:pagination.prev_title")}
              >
                <ChevronLeft className="w-4 h-4" />
              </button>

              <span className="text-xs sm:text-sm px-2">
                <strong>{page}</strong>
                {total ? <> / {Math.max(1, Math.ceil(total / PAGE_SIZE))}</> : null}
              </span>

              <button
                onClick={() => setPage(p => total ? Math.min(Math.ceil(total / PAGE_SIZE), p + 1) : p + 1)}
                disabled={total ? page >= totalPages : trainings.length < PAGE_SIZE}
                className="p-2 rounded border disabled:opacity-50 bg-white border-gray-200 hover:bg-gray-50 dark:bg-gray-900 dark:border-gray-800 dark:hover:bg-gray-800/60"
                title={t("alltrainings:pagination.next_title")}
              >
                <ChevronRight className="w-4 h-4" />
              </button>
              <button
                onClick={() => total && setPage(Math.max(1, Math.ceil(total / PAGE_SIZE)))}
                disabled={!total || page >= totalPages}
                className="hidden sm:inline-flex p-2 rounded border disabled:opacity-50 bg-white border-gray-200 hover:bg-gray-50 dark:bg-gray-900 dark:border-gray-800 dark:hover:bg-gray-800/60"
                title={t("alltrainings:pagination.last_title")}
              >
                <ChevronsRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
